/* THE THEATRE: what the bedside reads, in the order it is read. One central cell and rings around it, read from the
   centre outward on a fixed scan path (clock, four primary numerics, waveform strip, orders at this minute, the Resident's
   actions, world and run clocks, verdict so far); the problem list is the right ring, read after. The pattern is first
   (OK, degrading, failing) and the number is on demand. Pure: every function reads values the agent routes returned.

   Sources, so every number traces: the charted vitals (get_chart `vitals`), the alarm limits in force (alarms.ts), the
   chart's orders and their status, the end_case answers of the cases already handed back. The temperature band and the
   level of consciousness are the RCP NEWS2 (2017) bands, a display aid and not the world's scoring. */
import type { Chart, ChartRow } from '../../lib/types.ts'
import { limitsOf, type AlarmLimits, type Alarmed } from './alarms.ts'

/** The scan path, centre outward. The page draws the rings in this order, so the DOM order is the eye's order. */
export const SCAN_PATH = ['clock', 'numerics', 'trace', 'orders', 'actions', 'clocks', 'verdict'] as const
export type Ring = (typeof SCAN_PATH)[number]
export const RING_TEXT: Record<Ring, string> = {
  clock: 'World clock',
  numerics: 'Four numerics',
  trace: 'Waveform strip',
  orders: 'Orders at this minute',
  actions: 'The Resident’s actions',
  clocks: 'World and run clocks',
  verdict: 'Verdict so far',
}

/** The honesty label on the waveform channel. */
export const TRACE_LABEL = 'synthesised from model state'
export const TRACE_NOTE = 'The world has no electrodes. The trace is drawn from the charted rate, saturation and breathing; the numbers are what was measured.'

export type Pattern = 'ok' | 'degrading' | 'failing'
export const PATTERN_TEXT: Record<Pattern, string> = { ok: 'OK', degrading: 'Degrading', failing: 'Failing' }
const RANK: Record<Pattern, number> = { ok: 0, degrading: 1, failing: 2 }
export const worst = (ps: readonly Pattern[]): Pattern => ps.reduce<Pattern>((a, p) => (RANK[p] > RANK[a] ? p : a), 'ok')

export type Param = Alarmed | 'temp' | 'mental'
export const PARAM_ORDER: readonly Param[] = ['hr', 'map', 'spo2', 'rr', 'temp', 'mental']
/** The four numerics the eye reads first. */
export const PRIMARY: readonly Alarmed[] = ['hr', 'spo2', 'map', 'rr']

/** Colour by parameter, after the Philips IntelliVue assignment: ECG and temperature green, SpO2 and CVP cyan, ICP magenta. Arterial and NIBP are white and PAP, CO2 and respiration lavender, because red and yellow are the alarm priorities and nothing else at the bedside wears them. */
export const PHILIPS = { ecg: 'green', spo2: 'cyan', art: 'white', nibp: 'white', cvp: 'cyan', pap: 'lavender', co2: 'lavender', resp: 'lavender', icp: 'magenta', temp: 'green' } as const
export type Channel = keyof typeof PHILIPS

/** The channel a parameter is drawn on. The mean pressure is arterial when the chart says the patient has an arterial line, and a cuff otherwise. */
export function channelOf(p: Param, access: string): Channel | null {
  if (p === 'hr') return 'ecg'
  if (p === 'spo2') return 'spo2'
  if (p === 'rr') return 'resp'
  if (p === 'temp') return 'temp'
  if (p === 'map') return /arterial/i.test(access) ? 'art' : 'nibp'
  return null
}
export const CHANNEL_NAME: Record<Channel, string> = { ecg: 'ECG', spo2: 'SpO2', art: 'ART', nibp: 'NIBP', cvp: 'CVP', pap: 'PAP', co2: 'CO2', resp: 'RESP', icp: 'ICP', temp: 'TEMP' }

export const UNIT: Record<Exclude<Param, 'mental'>, string> = { hr: '/min', map: 'mmHg', spo2: '%', rr: '/min', temp: '°C' }
export const MENTAL = ['Alert', 'Confused', 'Responds to voice', 'Responds to pain'] as const

/** RCP NEWS2 (2017) temperature bands: 36.1 to 38.0 scores 0. Used as the temperature's band; the world sets no temperature limit. */
export const TEMP_BAND = { low: 36.1, high: 38.0 }

/** The band a parameter's number is read against: the alarm limits in force for the four the monitor limits, the NEWS2 band for temperature, the alert level for consciousness. */
export function bandOf(p: Param, l: AlarmLimits): { low: number | null; high: number | null; source: string } {
  if (p === 'temp') return { low: TEMP_BAND.low, high: TEMP_BAND.high, source: 'NEWS2 band' }
  if (p === 'mental') return { low: null, high: 0, source: 'alert' }
  const lim = limitsOf(p, l)
  return { low: lim.low, high: lim.high, source: 'alarm limits' }
}

/** H when above the band, L when below, an empty string inside it. */
export function flagOf(p: Param, value: number | null | undefined, l: AlarmLimits): '' | 'H' | 'L' {
  if (value === null || value === undefined || !Number.isFinite(value)) return ''
  const b = bandOf(p, l)
  if (b.low !== null && value < b.low) return 'L'
  if (b.high !== null && value > b.high) return 'H'
  return ''
}

export const valueOf = (row: ChartRow | null, p: Param): number | null => (row ? row[p].value : null)
export const atOf = (row: ChartRow | null, p: Param): number | undefined => (row ? row[p].at : undefined)

/** One parameter's pattern from its charted values, oldest first. Failing: outside its band now. Degrading: inside it and, over the last three
    observations, strictly closing on the nearer edge. A level of consciousness other than alert is failing (NEWS2 scores any new confusion at the top). */
export function patternOf(p: Param, values: readonly number[], l: AlarmLimits): Pattern {
  if (!values.length) return 'ok'
  const now = values[values.length - 1]
  if (p === 'mental') return now > 0 ? 'failing' : 'ok'
  const b = bandOf(p, l)
  if (flagOf(p, now, l) !== '') return 'failing'
  const edge = (v: number): number => {
    const toLow = b.low === null ? Infinity : v - b.low
    const toHigh = b.high === null ? Infinity : b.high - v
    return Math.min(toLow, toHigh)
  }
  if (values.length >= 3) {
    const [a, c, d] = values.slice(-3).map(edge)
    if (Number.isFinite(d) && a > c && c > d) return 'degrading'
  }
  return 'ok'
}

export function seriesOf(rows: readonly ChartRow[], p: Param): number[] {
  return rows.map((r) => r[p].value)
}

export type Stability = { overall: Pattern; segments: { param: Param; pattern: Pattern }[] }

/** The stability bar: one segment for each charted parameter, and the worst of them as the pattern. Before any observation every segment is OK and `read` says so. */
export function stabilityOf(rows: readonly ChartRow[], l: AlarmLimits): Stability & { read: boolean } {
  const segments = PARAM_ORDER.map((param) => ({ param, pattern: patternOf(param, seriesOf(rows, param), l) }))
  return { overall: worst(segments.map((s) => s.pattern)), segments, read: rows.length > 0 }
}

/** The ordered sedative infusions, worlds/001 vocabulary.ts (the four drip orders given from unit stock). */
export const SEDATIVES = ['propofol', 'midazolam', 'dexmedetomidine', 'fentanyl'] as const
/** The fluids given as a bolus, vocabulary.ts class `fluid`. */
export const FLUIDS = ['ns_bolus', 'lr_bolus', 'albumin_5', 'd5w'] as const
/** The largest bolus the order form takes (actions.ts: volume_ml up to 3000): the bar is full at that. */
export const FLUID_FULL_ML = 3000
const IN_BODY = new Set(['running', 'given', 'done'])

export type Burden = { fluidMl: number; fluidFill: number; sedatives: number; sedativesOf: number }

/** The slower bar: what has been put into the patient. Fluid is the volume of the bolus orders the chart shows as running, given or done; sedation is how many of the four sedative infusions are running. */
export function burdenOf(orders: Chart['orders']): Burden {
  let ml = 0
  for (const o of orders) {
    if (!(FLUIDS as readonly string[]).includes(o.order) || !IN_BODY.has(o.status)) continue
    const v = o.args.volume_ml
    if (typeof v === 'number' && Number.isFinite(v)) ml += v
  }
  const sed = new Set(orders.filter((o) => (SEDATIVES as readonly string[]).includes(o.order) && o.status === 'running').map((o) => o.order))
  return { fluidMl: ml, fluidFill: Math.min(1, ml / FLUID_FULL_ML), sedatives: sed.size, sedativesOf: SEDATIVES.length }
}

/** What the two halves of the burden bar count, said with the numbers the bar is drawn against. */
export const BURDEN_KEY = `fluid: bolus mL of the ${FLUID_FULL_ML.toLocaleString('en-US')} mL order maximum; sedation: sedative infusions running of ${SEDATIVES.length}`

/** Orders at this minute: not yet done or stopped, in the order they were placed. */
const SETTLED = new Set(['done', 'stopped', 'resulted', 'failed'])
export const activeOrders = (orders: Chart['orders']): Chart['orders'] => orders.filter((o) => !SETTLED.has(o.status))

/** What the cases handed back so far say, from their end_case answers: the verdict so far, pattern first. */
export type Handed = { died?: boolean; harms?: number; bundle?: { met: boolean }[] }
export type VerdictSoFar = { handed: number; planned: number; died: number; harms: number; metElements: number; elements: number; pattern: Pattern | null }

export function verdictSoFar(handed: readonly Handed[], planned: number): VerdictSoFar {
  const died = handed.filter((h) => h.died === true).length
  const harms = handed.reduce((n, h) => n + (typeof h.harms === 'number' ? h.harms : 0), 0)
  const bundle = handed.flatMap((h) => h.bundle ?? [])
  const pattern: Pattern | null = handed.length === 0 ? null : died > 0 ? 'failing' : harms > 0 ? 'degrading' : 'ok'
  return { handed: handed.length, planned, died, harms, metElements: bundle.filter((b) => b.met).length, elements: bundle.length, pattern }
}

/** How the sweep moves. A moving sweep is the essential animation of a monitor; with reduced motion it steps once a second, a whole second of strip (25 mm) at a time. */
export type Sweep = { mode: 'continuous' } | { mode: 'step'; everyMs: number; advanceMm: number }
export const STRIP_MM_PER_S = 25
export function sweepFor(reducedMotion: boolean): Sweep {
  return reducedMotion ? { mode: 'step', everyMs: 1000, advanceMm: STRIP_MM_PER_S } : { mode: 'continuous' }
}

/** The ECG ruling at 25 mm/s: 1 mm is 40 ms, 5 mm a heavy line (200 ms), 10 mm is 1 mV, and the calibration pulse is 1 mV high and 200 ms wide. */
export const RULING = { mmPerS: STRIP_MM_PER_S, msPerMm: 1000 / STRIP_MM_PER_S, mmPerMv: 10, majorEveryMm: 5, calibration: { mv: 1, ms: 200 } }
/** CSS pixels in a millimetre (96 dpi). */
export const PX_PER_MM = 96 / 25.4
